import React, { useState, useEffect } from 'react';

const Sidebar = ({
  doctorInfo,
  micGranted,
  permission,
  onEditPersonalInfo,
  isRecording,
  isPaused,
  onRequestMic,
  onStartRecording,
  onStopRecording,
  onTogglePause,
  onNavigateHome,
  patientInfoValid = false
}) => {
  const [recordingTime, setRecordingTime] = useState(0);

  // Track recording time - keep frozen when stopped
  useEffect(() => {
    let interval = null;
    if (isRecording && !isPaused) {
      interval = setInterval(() => {
        setRecordingTime(time => time + 1);
      }, 1000);
    }
    // Don't reset timer when stopped - keep it frozen

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording, isPaused]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  return (
    <div className="sidebar" style={{
      background: 'white',
      borderRight: '1px solid #e5e7eb',
      boxShadow: 'none'
    }}>
      <div className="sidebar-header" style={{
        padding: '24px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <button
          onClick={onNavigateHome}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            transition: 'opacity 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          title="Back to home"
        >
          <svg style={{ width: '32px', height: '32px', color: '#059669' }} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L21.5 7v10L12 22 2.5 17V7L12 2z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M9 9h6M12 6v12M9 15h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>MediScan</span>
        </button>
        <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '8px' }}>Intelligent medical transcription</p>
      </div>
      
      <div style={{ padding: '24px', flex: 1 }}>
        {/* Session Info */}
        {doctorInfo.name && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#6b7280',
              marginBottom: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.025em'
            }}>
              Session
            </h3>
            <div style={{
              background: '#f9fafb',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ marginBottom: '8px' }}>
                <span style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Physician
                </span>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#111827'
                }}>
                  {doctorInfo.name}
                </div>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <span style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Specialty
                </span>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#111827'
                }}>
                  {doctorInfo.specialty === 'autre' ? doctorInfo.customSpecialty : doctorInfo.specialty}
                </div>
              </div>
              <div>
                <span style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Location
                </span>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#111827'
                }}>
                  {doctorInfo.place}
                </div>
              </div>
            </div>
          </div>
        )}
         {/* Edit personal info button */}
        {doctorInfo.name && (
          <button 
            className="btn-secondary"
            onClick={onEditPersonalInfo}
            style={{ width: '100%', marginBottom: '16px' }}
            disabled={isRecording} // Freeze during recording
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Edit info
          </button>
        )}

        {/* Recording Controls */}
        <div style={{ marginBottom: '24px' }}>
          {/* Session Status Card */}
          <div style={{
            background: '#f8fafc',
            padding: '16px',
            borderRadius: '12px',
            marginBottom: '16px',
            border: '2px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Microphone Status */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: recordingTime > 0 ? '12px' : '0'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{
                  color: micGranted ? '#10b981' : '#64748b'
                }}>
                  <path d="M12 1c-1.1 0-2 .9-2 2v6c0 1.1.9 2 2 2s2-.9 2-2V3c0-1.1-.9-2-2-2z"/>
                  <path d="M19 10v2c0 3.9-3.1 7-7 7s-7-3.1-7-7v-2h2v2c0 2.8 2.2 5 5 5s5-2.2 5-5v-2h2z"/>
                  <path d="M11 19h2v3h-2z"/>
                </svg>
                <span style={{
                  fontSize: '13px',
                  fontWeight: '500',
                  color: micGranted ? '#10b981' : '#64748b'
                }}>
                  {micGranted ? 'Microphone allowed' : 'Microphone required'}
                </span>
              </div>
              {!micGranted && (
                <button
                  onClick={onRequestMic}
                  style={{
                    background: '#059669',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#047857'}
                  onMouseOut={(e) => e.target.style.background = '#059669'}
                >
                  Allow
                </button>
              )}
            </div>

            {/* Audio Visualization - Show when recording */}

            {/* Timer Display - Always show if there's been a session */}
            {recordingTime > 0 && (
              <>
                <div style={{
                  borderTop: '1px solid #e2e8f0',
                  paddingTop: '12px',
                  textAlign: 'center'
                }}>
                  {/* Subtle indicator bar */}
                  <div style={{
                    height: '2px',
                    background: isRecording ? (isPaused ? '#64748b' : '#059669') : '#e2e8f0',
                    borderRadius: '1px',
                    marginBottom: '12px',
                    opacity: isRecording ? 1 : 0.5
                  }}></div>

                  <div style={{
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#1e293b',
                    marginBottom: '4px',
                    fontFamily: 'ui-monospace, "SF Mono", "Monaco", "Cascadia Code", "Roboto Mono", monospace'
                  }}>
                    {formatTime(recordingTime)}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: isRecording ? (isPaused ? '#64748b' : '#059669') : '#94a3b8',
                      animation: isRecording && !isPaused ? 'pulse 3s infinite' : 'none'
                    }}></div>
                    {isRecording ? (isPaused ? 'Session paused' : 'Recording in progress') : 'Session ended'}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Recording Controls */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button
              onClick={isRecording ? onStopRecording : () => {
                if (!isRecording && recordingTime === 0) {
                  // Starting fresh session
                  onStartRecording();
                } else {
                  // Reset timer and start new session
                  setRecordingTime(0);
                  onStartRecording();
                }
              }}
              disabled={!micGranted || (!isRecording && !patientInfoValid)}
              title={!patientInfoValid && !isRecording ? 'Please fill in the patient information (first and last name)' : ''}
              style={{
                width: '100%',
                background: isRecording ? '#f8fafc' : '#10b981',
                border: isRecording ? '2px solid #e11d48' : '2px solid #d1fae5',
                borderRadius: '10px',
                padding: '14px',
                color: isRecording ? '#e11d48' : 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: (micGranted && (isRecording || patientInfoValid)) ? 'pointer' : 'not-allowed',
                transition: 'all 0.15s ease',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: (micGranted && (isRecording || patientInfoValid)) ? 1 : 0.5
              }}
              onMouseOver={(e) => {
                if (micGranted && (isRecording || patientInfoValid)) {
                  if (isRecording) {
                    e.target.style.background = '#fef2f2';
                    e.target.style.borderColor = '#dc2626';
                  } else {
                    e.target.style.background = '#059669';
                  }
                  e.target.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.15)';
                }
              }}
              onMouseOut={(e) => {
                if (micGranted && (isRecording || patientInfoValid)) {
                  if (isRecording) {
                    e.target.style.background = '#f8fafc';
                    e.target.style.borderColor = '#e11d48';
                  } else {
                    e.target.style.background = '#10b981';
                  }
                  e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                }
              }}
            >
              {isRecording ? (
                <>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <rect x="6" y="6" width="12" height="12" rx="2"/>
                  </svg>
                  End session
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <circle cx="12" cy="12" r="10"/>
                    <polygon points="10,8 16,12 10,16"/>
                  </svg>
                  {recordingTime > 0 ? 'New session' : 'Start session'}
                </>
              )}
            </button>

            {/* Pause Button - Only show when recording */}
            {isRecording && (
              <button 
                onClick={onTogglePause}
                style={{
                  width: '100%',
                  background: '#f8fafc',
                  border: isPaused ? '2px solid #10b981' : '2px solid #64748b',
                  borderRadius: '10px',
                  padding: '12px',
                  color: isPaused ? '#10b981' : '#64748b',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = isPaused ? '#f0fdf4' : '#f1f5f9';
                  e.target.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.15)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#f8fafc';
                  e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                }}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  {isPaused ? (
                    <polygon points="5,3 19,12 5,21"/>
                  ) : (
                    <>
                      <rect x="6" y="4" width="4" height="16"/>
                      <rect x="14" y="4" width="4" height="16"/>
                    </>
                  )}
                </svg>
                {isPaused ? 'Resume session' : 'Pause'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;